/**
 * The "+" menu — the concept's `flow/AddMenu.jsx`, typed, wired to real data.
 *
 * Root, top to bottom as the reference has it: **Suggest next step** first and
 * highlighted (it calls `POST /flows/:id/suggest`, a deterministic rule, and
 * whatever comes back lands dashed, for a person to accept), Send a message,
 * Message template ›, Add logic ›, Logic template ›, Profile update, Goal,
 * Exit, Add a comment. One addition over the reference: **Go to an existing
 * step ›**, because a real flow merges paths (a reply and a no-reply both
 * leading to the same Wait) and the reference could only draw that in its
 * sample data. The list offers only steps that cannot make a loop.
 *
 * Message templates are the APPROVED ones the API listed; pending and
 * rejected never reach this menu, and the footer says how many are hidden so
 * the person knows where theirs went.
 *
 * A real ARIA menu: arrow keys, Home/End, Escape backs out of a submenu then
 * closes, Tab closes and hands focus back to the "+" it came from.
 */
import { cn } from '@wend/ui';
import { useEffect, useRef, type ComponentType, type KeyboardEvent } from 'react';

import {
  IcBack,
  IcChat,
  IcClock,
  IcComment,
  IcCond,
  IcExit,
  IcFile,
  IcFileLogic,
  IcFlag,
  IcLink,
  IcMail,
  IcNext,
  IcPerson,
  IcSpark,
  IcSplit,
  type IconProps,
} from './icons';
import { LOGIC_TEMPLATES, type FlowTemplateOption, type LogicTemplateId } from './model';

export type AddPick =
  | { kind: 'suggest' }
  | { kind: 'message'; channel: 'whatsapp' | 'email'; templateId: string | null }
  | { kind: 'step'; step: 'condition' | 'split' | 'wait' | 'update' | 'goal' | 'exit' | 'comment' }
  | { kind: 'logictpl'; id: LogicTemplateId }
  | { kind: 'connect'; to: string };

export type AddMenuView = 'root' | 'logic' | 'msgtpl' | 'logictpl' | 'connect';

interface ItemProps {
  icon: ComponentType<IconProps>;
  label: string;
  hint?: string;
  onClick: () => void;
  tone?: 'ai' | 'muted';
  sub?: boolean;
  disabled?: boolean;
}

function Item({ icon: Icon, label, hint, onClick, tone, sub, disabled }: ItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={cn(
        'flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent px-3 py-2.5 text-left text-[14px] text-text',
        'hover:bg-white/[0.07] focus-visible:bg-white/[0.07] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-b',
        tone === 'ai' && 'rounded-b-[4px] border-b border-brand-b/35 bg-brand-a/17 font-medium text-[#DCD3FF] hover:bg-brand-a/28 focus-visible:bg-brand-a/28',
        tone === 'muted' && 'text-muted',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Icon size={17} className={cn('flex-none', tone === 'ai' ? 'text-[#B9A6FF]' : 'text-muted')} />
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        {label}
        {hint && <small className="block text-[11.5px] text-muted">{hint}</small>}
      </span>
      {sub && <IcNext size={14} className="ml-auto text-muted" />}
    </button>
  );
}

const Sep = () => <div role="separator" className="mx-1 my-[5px] h-px bg-line" />;

export interface AddMenuProps {
  /** Screen position inside the viewport; omitted when the menu sits inline (the list view). */
  x?: number;
  y?: number;
  view: AddMenuView;
  setView: (view: AddMenuView) => void;
  onPick: (pick: AddPick) => void;
  onClose: (returnFocus: boolean) => void;
  portLabel: string;
  templates: readonly FlowTemplateOption[];
  /** Templates that exist but are not approved, so not offered. */
  hiddenTemplates: number;
  targets: readonly { id: string; label: string }[];
  suggesting: boolean;
}

export function AddMenu({ x, y, view, setView, onPick, onClose, portLabel, templates, hiddenTemplates, targets, suggesting }: AddMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [view]);

  const items = () => [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (view !== 'root') setView('root');
      else onClose(true);
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      onClose(true);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const all = items();
      all[event.key === 'Home' ? 0 : all.length - 1]?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const all = items();
      const i = all.indexOf(document.activeElement as HTMLElement);
      const next = event.key === 'ArrowDown' ? (i + 1) % all.length : (i - 1 + all.length) % all.length;
      all[next]?.focus();
    }
  };

  const back = (title: string) => (
    <div className="mb-1 flex items-center gap-2 border-b border-line px-0.5 pb-[7px] pt-0.5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted">
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        aria-label="Back to all options"
        onClick={() => setView('root')}
        className="grid size-[26px] cursor-pointer place-items-center rounded-[7px] border-0 bg-white/[0.06] p-0 text-text focus-visible:outline-2 focus-visible:outline-brand-b"
      >
        <IcBack size={14} />
      </button>
      <span>{title}</span>
    </div>
  );

  const wa = templates.filter((t) => t.channel === 'whatsapp');
  const email = templates.filter((t) => t.channel === 'email');

  let content;
  if (view === 'logic') {
    content = (
      <>
        {back('Add logic')}
        <Item icon={IcCond} label="Condition" hint="Yes / No on any field or journey event" onClick={() => onPick({ kind: 'step', step: 'condition' })} />
        <Item icon={IcSplit} label="Random split" hint="Paths by percentage, with a holdout" onClick={() => onPick({ kind: 'step', step: 'split' })} />
        <Item icon={IcClock} label="Wait" hint="A duration, until a date, or the next time of day" onClick={() => onPick({ kind: 'step', step: 'wait' })} />
      </>
    );
  } else if (view === 'msgtpl') {
    content = (
      <>
        {back('Approved templates')}
        {wa.map((t) => (
          <Item key={t.id} icon={IcChat} label={t.name} hint="WhatsApp" onClick={() => onPick({ kind: 'message', channel: 'whatsapp', templateId: t.id })} />
        ))}
        {email.map((t) => (
          <Item key={t.id} icon={IcMail} label={t.name} hint={t.subject ? `Email · ${t.subject}` : 'Email'} onClick={() => onPick({ kind: 'message', channel: 'email', templateId: t.id })} />
        ))}
        {templates.length === 0 && <p className="m-0 px-2.5 py-2 text-[12.5px] text-muted">No approved templates yet. Approve one under Templates first.</p>}
        {hiddenTemplates > 0 && (
          <p className="m-0 px-2.5 pb-1 pt-1.5 text-[11.5px] text-muted">
            {hiddenTemplates} more hidden — drafts, pending review or rejected.
          </p>
        )}
      </>
    );
  } else if (view === 'logictpl') {
    content = (
      <>
        {back('Logic templates')}
        {LOGIC_TEMPLATES.map((t) => (
          <Item key={t.id} icon={IcFileLogic} label={t.label} hint={t.hint} onClick={() => onPick({ kind: 'logictpl', id: t.id })} />
        ))}
      </>
    );
  } else if (view === 'connect') {
    content = (
      <>
        {back('Go to an existing step')}
        {targets.map((t) => (
          <Item key={t.id} icon={IcLink} label={t.label} onClick={() => onPick({ kind: 'connect', to: t.id })} />
        ))}
        {targets.length === 0 && <p className="m-0 px-2.5 py-2 text-[12.5px] text-muted">No step can follow here without making a loop.</p>}
      </>
    );
  } else {
    content = (
      <>
        <Item icon={IcSpark} label={suggesting ? 'Suggesting…' : 'Suggest next step'} tone="ai" disabled={suggesting} onClick={() => onPick({ kind: 'suggest' })} />
        <Sep />
        <Item icon={IcChat} label="Send a message" hint="WhatsApp" onClick={() => onPick({ kind: 'message', channel: 'whatsapp', templateId: wa[0]?.id ?? null })} />
        <Item icon={IcFile} label="Message template" sub onClick={() => setView('msgtpl')} />
        <Sep />
        <Item icon={IcSplit} label="Add logic" sub onClick={() => setView('logic')} />
        <Item icon={IcFileLogic} label="Logic template" sub onClick={() => setView('logictpl')} />
        <Sep />
        <Item icon={IcPerson} label="Profile update" hint="Set a field or a tag" onClick={() => onPick({ kind: 'step', step: 'update' })} />
        <Item icon={IcFlag} label="Goal" hint="Counts a conversion and ends here" onClick={() => onPick({ kind: 'step', step: 'goal' })} />
        <Item icon={IcExit} label="Exit" hint="Ends the path, with a cool-off" onClick={() => onPick({ kind: 'step', step: 'exit' })} />
        <Item icon={IcLink} label="Go to an existing step" sub onClick={() => setView('connect')} />
        <Sep />
        <Item icon={IcComment} label="Add a comment" tone="muted" onClick={() => onPick({ kind: 'step', step: 'comment' })} />
      </>
    );
  }

  const inline = x === undefined || y === undefined;
  return (
    <div
      ref={ref}
      role="menu"
      aria-label={`Add a step after “${portLabel}”`}
      data-flow-menu=""
      style={inline ? undefined : { left: x, top: y }}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (event.relatedTarget && !ref.current?.contains(event.relatedTarget as Node)) onClose(false);
      }}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      className={cn(
        'z-20 w-64 rounded-xl border border-line-strong bg-surface-2 p-1.5 shadow-[0_26px_54px_-16px_rgba(0,0,0,.9)] motion-safe:animate-pop',
        inline ? 'relative mt-2 w-full' : 'absolute',
      )}
    >
      {content}
    </div>
  );
}
